import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';
import { TextElement } from '../models/TranscriptElement';

/**
 * Command to update the text value of a transcript element
 * 
 * This command changes the text content of a text element while
 * preserving the timing information and other properties.
 */
export class UpdateElementTextCommand extends AbstractTranscriptEditorCommand {
  /** ID of the segment containing the element */
  private segmentId: string;
  
  /** ID of the paragraph containing the element */
  private paragraphId: string;
  
  /** ID of the element to update */
  private elementId: string;
  
  /** New text content for the element */
  private newText: string;
  
  /** Original text content for undo operations */
  private oldText: string = '';
  
  /** User ID for operation tracking */
  private userId: string;
  
  /**
   * Creates a new UpdateElementTextCommand
   * 
   * @param editor Reference to the transcript editor core
   * @param segmentId ID of the segment containing the element
   * @param paragraphId ID of the paragraph containing the element
   * @param elementId ID of the element to update
   * @param newText New text content
   * @param userId ID of the user making the change
   */
  constructor(
    editor: TranscriptEditorCore,
    segmentId: string,
    paragraphId: string,
    elementId: string,
    newText: string,
    userId: string
  ) {
    super(`Update text for element ${elementId}`, editor);
    
    this.segmentId = segmentId;
    this.paragraphId = paragraphId;
    this.elementId = elementId;
    this.newText = newText;
    this.userId = userId;
    
    // Store metadata for debugging and tracking
    this.setMetadata('segmentId', segmentId);
    this.setMetadata('paragraphId', paragraphId);
    this.setMetadata('elementId', elementId);
    this.setMetadata('userId', userId);
    this.setMetadata('newTextLength', newText.length);
  }
  
  /**
   * Execute the command
   * 
   * @returns Promise resolving to true if successful
   */
  async execute(): Promise<boolean> {
    try {
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      // Get the segment
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        return false;
      }
      
      // Get the paragraph
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) {
        return false;
      }
      
      // Get the element
      const element = paragraph.getElement(this.elementId);
      if (!element || element.type !== 'text') {
        return false;
      }
      
      // Store old text for undo
      this.oldText = (element as TextElement).value;
      this.setMetadata('oldTextLength', this.oldText.length);
      
      // Check if the new text is empty, which means we should delete the element
      if (this.newText.trim() === '') {
        // Delegate to deleteElement method
        return this.editor.deleteElement(this.segmentId, this.paragraphId, this.elementId, this.userId);
      }
      
      // Update the element text
      (element as TextElement).value = this.newText;
      
      // Increment element counter
      element.incrementCounter();
      
      // Force MobX reactivity by incrementing counters up the hierarchy
      paragraph.incrementCounter();
      segment.incrementCounter();
      container.incrementCounter();
      
      // Record the operation
      this.editor.recordOperation({
        type: 'text_edit',
        segmentId: this.segmentId,
        paragraphId: this.paragraphId,
        elementId: this.elementId,
        data: {
          oldText: this.oldText,
          newText: this.newText
        },
        userId: this.userId,
        timestamp: Date.now()
      });
      
      // Notify observers of the change
      this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_UPDATED, {
        segment,
        paragraph,
        element,
        oldText: this.oldText,
        newText: this.newText
      });
      
      return true;
    } catch (error) {
      console.error('Error executing UpdateElementTextCommand:', error);
      
      return false;
    }
  }
  
  /**
   * Undo the command
   * 
   * @returns Promise resolving to true if successful
   */
  async undo(): Promise<boolean> {
    
    try {
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      // Get the segment
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        return false;
      }
      
      // Get the paragraph
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) {
        return false;
      }
      
      // Get the element
      const element = paragraph.getElement(this.elementId);
      if (!element || element.type !== 'text') {
        return false;
      }
      
      // Restore the old text
      (element as TextElement).value = this.oldText;
      
      // Notify observers of the undo
      this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_UPDATED, {
        segment,
        paragraph,
        element,
        oldText: this.newText,
        newText: this.oldText
      });
      
      return true;
    } catch (error) {
      console.error('Error undoing UpdateElementTextCommand:', error);
      
      return false;
    }
  }
}